import React, { useRef, useState, useEffect } from 'react';
import Croppie from 'croppie';
import 'croppie/croppie.css';
import { Button } from '../common/ui/Button';
import { captureAppException } from '../../utils/errorCaptureUtils';

interface WorkspaceImageUploadProps {
  currentImageUrl?: string;
  onImageUpload: (file: File) => Promise<void>;
}

export const WorkspaceImageUpload: React.FC<WorkspaceImageUploadProps> = ({
  currentImageUrl,
  onImageUpload
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewObjectUrlRef = useRef<string | null>(null);

  const croppieContainerRef = useRef<HTMLDivElement | null>(null);
  const croppieInstanceRef = useRef<Croppie | null>(null);
  const [recalcSize, setRecalcSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    setPreviewUrl(currentImageUrl || null);
  }, [currentImageUrl]);

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      captureAppException('File size exceeds limit', {
        tags: {
          level: 'warning',
          context: 'workspace_image_upload',
          action: 'file_validation',
          file_size: file.size,
          file_type: file.type
        }
      });
      alert('File must be under 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      captureAppException('Invalid file type', {
        tags: {
          level: 'warning',
          context: 'workspace_image_upload',
          action: 'file_validation',
          file_type: file.type
        }
      });
      alert('Only images allowed');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        setSelectedImage(reader.result as string);
        setShowModal(true);
      }
    };
    reader.onerror = (error) => {
      captureAppException(error, {
        tags: {
          context: 'workspace_image_upload',
          action: 'read_file',
          file_type: file.type,
          file_size: file.size
        }
      });
      alert('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const initCroppie = (opts?: Partial<Croppie.CroppieOptions>) => {
    if (!croppieContainerRef.current) return;

    if (croppieInstanceRef.current) {
      croppieInstanceRef.current.destroy();
      croppieInstanceRef.current = null;
    }

    croppieInstanceRef.current = new Croppie(croppieContainerRef.current, {
      enableExif: true,
      enableOrientation: false,
      enableZoom: true,
      showZoomer: true,
      ...opts
    });
  };

  useEffect(() => {
    if (!showModal || !croppieContainerRef.current || !selectedImage) return;

    const containerWidth = croppieContainerRef.current.offsetWidth;
    if (!containerWidth) return;

    const boundarySize = containerWidth;
    const viewportSize = Math.floor(boundarySize * 0.8);

    initCroppie({
      boundary: { width: boundarySize, height: boundarySize },
      viewport: { width: viewportSize, height: viewportSize, type: 'square' }
    });

    croppieInstanceRef.current?.bind({ url: selectedImage });
  }, [showModal, selectedImage, recalcSize]);

  useEffect(() => {
    function handleResize() {
      if (showModal) {
        setRecalcSize({ w: window.innerWidth, h: window.innerHeight });
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showModal]);

  const handleSave = async () => {
    if (!croppieInstanceRef.current) return;
    setLoading(true);
    try {
      const blob = await croppieInstanceRef.current.result({
        type: 'blob',
        format: 'png',
        size: { width: 500, height: 500 },
        circle: true
      });

      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const filename = `logo-${timestamp}-${randomString}.png`;

      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }

      const file = new File([blob], filename, { type: 'image/png' });
      const objectUrl = URL.createObjectURL(blob);
      previewObjectUrlRef.current = objectUrl;
      setPreviewUrl(objectUrl);

      // Wait for image upload to complete
      await onImageUpload(file);
      
      setShowModal(false);
      setSelectedImage(null);
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'workspace_image_upload',
          action: 'crop_and_save',
          error_step: err instanceof Error && err.message.includes('Croppie') ? 'crop' : 'upload'
        }
      });
      console.error('Croppie error:', err);
      alert('Failed to crop image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-4">
            <h3 className="text-base font-medium mb-4">Crop Logo</h3>
            <div ref={croppieContainerRef} className="w-full min-h-[200px]" />
            <div className="flex justify-end mt-4 gap-2">
              <Button
                color="secondary"
                variant="outline"
                size="small"
                state={loading ? 'loading' : 'default'}
                onClick={() => {
                  setShowModal(false);
                  setSelectedImage(null);
                  if (previewObjectUrlRef.current) {
                    URL.revokeObjectURL(previewObjectUrlRef.current);
                    previewObjectUrlRef.current = null;
                    setPreviewUrl(currentImageUrl || null);
                  }
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="filled"
                size="small"
                state={loading ? 'loading' : 'default'}
                onClick={handleSave}
                disabled={loading}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
        <div className="relative flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full overflow-hidden">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="workspace logo"
              className="w-full h-full object-cover"
            />
          ) : (
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          )}
        </div>

        <Button
          color="primary"
          variant="filled"
          size="small"
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.addEventListener('change', (e) =>
              handleFileSelect(e as unknown as React.ChangeEvent<HTMLInputElement>)
            );
            input.click();
          }}
        >
          Upload Logo
        </Button>
      </div>
    </>
  );
};